import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PriceQuoteForm } from '@/components/price-quote-form';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency } from '@/lib/currency';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger 
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ResponsiveTable, MobileValue } from '@/components/ui/responsive-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { format } from 'date-fns';
import { 
  FileText, 
  Printer, 
  Send, 
  Download, 
  Pencil, 
  Trash2,
  Plus,
  Check,
  X,
  FileDown,
  Repeat,
  Search,
  ArrowUpDown
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// QR code import odstránený
import { insertPriceQuoteSchema, Client, PriceQuote, Invoice, PriceQuoteItem } from '@shared/schema';
import { apiRequest } from '@/lib/queryClient';

// Funkcia na formátovanie čísla cenovej ponuky v štandardizovanom formáte CP-ROK-CISLO
function formatPriceQuoteNumber(id: number): string {
  const currentYear = new Date().getFullYear();
  return `CP-${currentYear}-${id.toString().padStart(5, '0')}`;
}

// Extended PriceQuote type with items for client-side use
interface PriceQuoteWithItems extends PriceQuote {
  items?: PriceQuoteItem[];
}


// formatCurrency now imported from centralized utility





// PriceQuoteTable component interface
interface PriceQuoteTableProps {
  priceQuotes: PriceQuote[];
  clients: Client[];
  onPreview: (priceQuote: PriceQuote) => void;
  onDelete: (priceQuote: PriceQuote) => void;
  onEmail: (priceQuote: PriceQuote) => void;
  onEdit: (priceQuote: PriceQuote) => void;
  onMarkAsAccepted: (priceQuoteId: number) => void;
  onMarkAsRejected: (priceQuoteId: number) => void;
  onConvertToInvoice: (priceQuote: PriceQuote) => void;
  onDuplicate: (priceQuote: PriceQuote) => void;
}

// PriceQuoteTable component
function PriceQuoteTable({ 
  priceQuotes, 
  clients,
  onPreview,
  onDelete,
  onEmail,
  onEdit,
  onMarkAsAccepted,
  onMarkAsRejected,
  onConvertToInvoice,
  onDuplicate
}: PriceQuoteTableProps) {
  const { t } = useTranslation(['priceQuotes', 'common']);
  
  // Helper function to get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft':
        return t('quotes:draft');
      case 'sent':
        return t('quotes:sent');
      case 'accepted':
        return t('quotes:accepted');
      case 'rejected':
        return t('quotes:rejected');
      case 'expired':
        return t('quotes:expired');
      default:
        return status;
    }
  };

  // Helper function to get status badge color
  const getStatusBadge = (status: string) => {
    const statusText = getStatusText(status);
    switch (status) {
      case 'draft':
        return <Badge variant="outline">{statusText}</Badge>;
      case 'sent':
        return <Badge variant="secondary">{statusText}</Badge>;
      case 'accepted':
        return <Badge className="bg-green-500 text-white">{statusText}</Badge>;
      case 'rejected':
        return <Badge variant="destructive">{statusText}</Badge>;
      case 'expired':
        return <Badge variant="destructive">{statusText}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('priceQuotes:title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {priceQuotes.length === 0 ? (
          <p className="text-center py-4 text-gray-500">{t('priceQuotes:noPriceQuotes')}</p>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">{t('priceQuotes:number')}</TableHead>
                  <TableHead className="w-[150px]">{t('priceQuotes:client')}</TableHead>
                  <TableHead className="hidden md:table-cell w-[120px]">{t('priceQuotes:date')}</TableHead>
                  <TableHead className="hidden md:table-cell w-[120px]">{t('priceQuotes:validUntil')}</TableHead>
                  <TableHead className="w-[100px]">{t('priceQuotes:status')}</TableHead>
                  <TableHead className="w-[100px]">{t('priceQuotes:amount')}</TableHead>
                  <TableHead className="text-right w-[150px]">{t('common:actionsLabel')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.isArray(priceQuotes) ? priceQuotes.map((priceQuote) => {
              const client = Array.isArray(clients) ? clients.find(c => c.id === priceQuote.clientId) : null;
              return (
                <TableRow key={priceQuote.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <span>{priceQuote.quoteNumber || formatPriceQuoteNumber(priceQuote.id)}</span>
                      {(priceQuote.convertedToInvoiceId || (priceQuote as any).converted_to_invoice_id) && (
                        <Badge variant="secondary">
                          Faktúra {priceQuote.invoiceNumber || (priceQuote as any).invoice_number || (priceQuote.convertedToInvoiceId || (priceQuote as any).converted_to_invoice_id)}
                        </Badge>
                      )}
                    </div>
                    <MobileValue label={t('priceQuotes:client')}>
                      {client?.businessName || client?.name || t('common:unknownClient')}
                    </MobileValue>
                    <MobileValue label={t('priceQuotes:date')}>
                      {format(new Date(priceQuote.createdAt || Date.now()), 'dd.MM.yyyy')}
                    </MobileValue>
                    <MobileValue label={t('priceQuotes:validUntil')}>
                      {priceQuote.validUntil ? format(new Date(priceQuote.validUntil), 'dd.MM.yyyy') : format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'dd.MM.yyyy')}
                    </MobileValue>
                  </TableCell>
                  <TableCell>{client?.businessName || client?.name || t('common:unknownClient')}</TableCell>
                  <TableCell className="hidden md:table-cell">{format(new Date(priceQuote.createdAt || Date.now()), 'dd.MM.yyyy')}</TableCell>
                  <TableCell className="hidden md:table-cell">{priceQuote.validUntil ? format(new Date(priceQuote.validUntil), 'dd.MM.yyyy') : format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'dd.MM.yyyy')}</TableCell>
                  <TableCell>{getStatusBadge(priceQuote.status)}</TableCell>
                  <TableCell>{formatCurrency(Number(priceQuote.totalAmount || priceQuote.total_amount || 0))}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onPreview(priceQuote)}
                          title={t('common:view')}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        
                        {priceQuote.status === 'draft' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEdit(priceQuote)}
                              title={t('common:edit')}
                            >
                              <Pencil className="h-4 w-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEmail(priceQuote)}
                              title={t('common:send')}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDelete(priceQuote)}
                              title={t('common:delete')}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                        
                        {priceQuote.status === 'draft' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEmail(priceQuote)}
                            title={t('priceQuotes:markAsSent')}
                            className="bg-blue-50 hover:bg-blue-100"
                          >
                            <Send className="h-4 w-4 text-blue-500" />
                          </Button>
                        )}
                        
                        {priceQuote.status === 'sent' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onMarkAsAccepted(priceQuote.id)}
                              title={t('priceQuotes:markAsAccepted')}
                              className="bg-green-50 hover:bg-green-100"
                            >
                              <Check className="h-4 w-4 text-green-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onMarkAsRejected(priceQuote.id)}
                              title={t('priceQuotes:markAsRejected')}
                              className="bg-red-50 hover:bg-red-100"
                            >
                              <X className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                        
                        {/* Tlačidlo pre duplikovanie */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDuplicate(priceQuote)}
                          title={t('priceQuotes:duplicate')}
                        >
                          <FileDown className="h-4 w-4 text-purple-500" />
                        </Button>
                        
                        {/* Tlačidlo pre konverziu na faktúru */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onConvertToInvoice(priceQuote)}
                          title={t('priceQuotes:convertToInvoice')}
                        >
                          <Repeat className="h-4 w-4 text-blue-500" />
                        </Button>
                        
                        {/* Tlačidlo pre vymazanie */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(priceQuote)}
                          title={t('common:delete')}
                          disabled={Boolean(priceQuote.convertedToInvoiceId)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
                }) : null}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PriceQuotes() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['priceQuotes', 'quotes', 'common']);
  
  // Helper function to get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft':
        return t('quotes:draft');
      case 'sent':
        return t('quotes:sent');
      case 'accepted':
        return t('quotes:accepted');
      case 'rejected':
        return t('quotes:rejected');
      case 'expired':
        return t('quotes:expired');
      default:
        return status;
    }
  };
  
  const [selectedPriceQuote, setSelectedPriceQuote] = useState<PriceQuoteWithItems | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [isPrintingToPdf, setIsPrintingToPdf] = useState(false);
  const priceQuotePreviewRef = useRef<HTMLDivElement>(null);

  // Debug: Track dialog state changes
  useEffect(() => {
    console.log('isCreateDialogOpen changed to:', isCreateDialogOpen);
  }, [isCreateDialogOpen]);

  // Fix pointer-events issue when dialog is open and scroll to top
  useEffect(() => {
    if (!isCreateDialogOpen) return;
    
    const fixPointerEvents = () => {
      // Ensure dialog content and all interactive elements are clickable
      const dialog = document.querySelector('[role="dialog"][data-state="open"]');
      if (dialog) {
        (dialog as HTMLElement).style.pointerEvents = 'auto';
        // Also fix all interactive elements inside dialog
        const interactiveElements = dialog.querySelectorAll('button, input, select, textarea, [role="button"], [role="menuitem"], [data-radix-select-trigger], [data-radix-popover-trigger]');
        interactiveElements.forEach((el) => {
          (el as HTMLElement).style.pointerEvents = 'auto';
        });
      }
      
      // Fix Select and Popover portals
      const selectContent = document.querySelector('[data-radix-select-content]');
      if (selectContent) {
        (selectContent as HTMLElement).style.pointerEvents = 'auto';
        (selectContent as HTMLElement).style.zIndex = '101';
      }
      
      const popoverContent = document.querySelector('[data-radix-popover-content]');
      if (popoverContent) {
        (popoverContent as HTMLElement).style.pointerEvents = 'auto';
        (popoverContent as HTMLElement).style.zIndex = '101';
      }
      
      // Fix body pointer-events
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = 'auto';
      }
    };
    
    // Fix immediately
    fixPointerEvents();
    
    // Scroll to top of dialog content when it opens
    const scrollToTop = () => {
      const dialog = document.querySelector('[role="dialog"][data-state="open"]');
      if (dialog) {
        const scrollableContent = dialog.querySelector('.overflow-y-auto');
        if (scrollableContent) {
          scrollableContent.scrollTop = 0;
        }
      }
    };
    
    // Try multiple times to ensure scroll happens after render
    setTimeout(scrollToTop, 50);
    setTimeout(scrollToTop, 200);
    setTimeout(scrollToTop, 500);
    
    // Fix on interval to catch any Radix UI changes
    const interval = setInterval(fixPointerEvents, 100);
    
    return () => clearInterval(interval);
  }, [isCreateDialogOpen]);
  
  // Search, sort and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'client' | 'amount'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterYear, setFilterYear] = useState<string>('all');
  
  // Fetch user data for company information
  const { data: userData } = useQuery({
    queryKey: ['/api/user'],
    queryFn: async () => {
      const res = await fetch('/api/user');
      if (!res.ok) throw new Error('Failed to fetch user data');
      return res.json();
    }
  });
  
  // Fetch price quotes
  const { data: priceQuotes, isLoading, error } = useQuery({
    queryKey: ['/api/price-quotes'],
    queryFn: async () => {
      const res = await fetch('/api/price-quotes');
      if (!res.ok) {
        throw new Error('Failed to fetch price quotes');
      }
      const data = await res.json() as PriceQuote[];
      
      return data;
    }
  });

  // Fetch clients
  const { data: clients } = useQuery({
    queryKey: ['/api/clients'],
    queryFn: async () => {
      const res = await fetch('/api/clients');
      if (!res.ok) throw new Error('Failed to fetch clients');
      const data = await res.json();
      return data.items || data; // Handle both { items: [...] } and [...] formats
    }
  });

  // Filter and sort price quotes
  const filteredAndSortedPriceQuotes = React.useMemo(() => {
    if (!priceQuotes || !clients) return [];

    let filtered = priceQuotes.filter((quote: PriceQuote) => {
      // Search term filter
      if (searchTerm) {
        const client = Array.isArray(clients) ? clients.find(c => c.id === quote.clientId) : null;
        const clientName = client ? (client.businessName || client.name).toLowerCase() : '';
        const title = quote.title.toLowerCase();
        const searchLower = searchTerm.toLowerCase();
        
        if (!title.includes(searchLower) && !clientName.includes(searchLower)) {
          return false;
        }
      }

      // Year filter
      if (filterYear !== 'all') {
        const quoteYear = new Date(quote.createdAt || '').getFullYear().toString();
        if (quoteYear !== filterYear) {
          return false;
        }
      }

      return true;
    });

    // Sort
    filtered.sort((a: PriceQuote, b: PriceQuote) => {
      let comparison = 0;

      switch (sortBy) {
        case 'date':
          comparison = new Date(a.createdAt || '').getTime() - new Date(b.createdAt || '').getTime();
          break;
        case 'client':
          const clientA = clients.find(c => c.id === a.clientId);
          const clientB = clients.find(c => c.id === b.clientId);
          const nameA = clientA ? (clientA.businessName || clientA.name) : '';
          const nameB = clientB ? (clientB.businessName || clientB.name) : '';
          comparison = nameA.localeCompare(nameB);
          break;
        case 'amount':
          comparison = parseFloat(a.totalAmount || '0') - parseFloat(b.totalAmount || '0');
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [priceQuotes, clients, searchTerm, sortBy, sortOrder, filterYear]);

  // Get unique years for filter
  const availableYears = React.useMemo(() => {
    if (!priceQuotes) return [];
    const years = Array.isArray(priceQuotes) ? priceQuotes.map((quote: PriceQuote) => 
      new Date(quote.createdAt || '').getFullYear()
    ) : [];
    return Array.from(new Set(years)).sort((a, b) => b - a);
  }, [priceQuotes]);

  // Delete price quote mutation
  const deleteMutation = useMutation({
    mutationFn: async (priceQuoteId: number) => {
      return apiRequest('DELETE', `/api/price-quotes/${priceQuoteId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/price-quotes'] });
      toast({
        title: t('priceQuotes:deleted'),
        description: t('priceQuotes:deletedSuccess'),
      });
      setDeleteDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: t('common:error'),
        description: `${t('priceQuotes:deleteFailed')}: ${error.message}`,
        variant: 'destructive',
      });
    }
  });

  // Update price quote status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ 
      priceQuoteId, 
      status, 
      email 
    }: { 
      priceQuoteId: number; 
      status: string; 
      email?: string 
    }) => {
      return apiRequest('POST', `/api/price-quotes/${priceQuoteId}/status`, { status, email });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/price-quotes'] });
      
      if (variables.status === 'sent' && variables.email) {
        toast({
          title: t('priceQuotes:emailSent'),
          description: t('priceQuotes:emailSentDescription', { email: variables.email }),
        });
      } else {
        toast({
          title: t('priceQuotes:statusUpdated'),
          description: t('priceQuotes:statusUpdatedDescription'),
        });
      }
      setIsEmailDialogOpen(false);
      setEmailAddress(''); // Clear email field for next use
    },
    onError: (error: Error) => {
      toast({
        title: t('common:error'),
        description: t('priceQuotes:operationFailed', { error: error.message }),
        variant: 'destructive',
      });
    }
  });

  // Convert to invoice mutation
  const convertToInvoiceMutation = useMutation({
    mutationFn: async (priceQuoteId: number) => {
      const response = await apiRequest('POST', `/api/price-quotes/${priceQuoteId}/convert-to-invoice`, {});
      return response as any; // Typecasting to avoid type errors
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/price-quotes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/invoices'] });
      toast({
        title: t('priceQuotes:convertedToInvoice'),
        description: t('priceQuotes:convertedToInvoiceSuccess'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('common:error'),
        description: `${t('priceQuotes:conversionFailed')}: ${error.message}`,
        variant: 'destructive',
      });
    }
  });
  
  // Duplicate price quote mutation
  const duplicatePriceQuoteMutation = useMutation({
    mutationFn: async (priceQuoteId: number) => {
      const response = await apiRequest('POST', `/api/price-quotes/${priceQuoteId}/duplicate`, {});
      return response as any;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/price-quotes'] });
      toast({
        title: t('priceQuotes:duplicated'),
        description: t('priceQuotes:duplicatedSuccess'),
      });
    },
    onError: (error: any) => {
      toast({
        title: t('priceQuotes:duplicateError'),
        description: error?.message || t('priceQuotes:duplicateErrorDescription'),
        variant: 'destructive',
      });
    }
  });
  
  // Create price quote mutation
  const createPriceQuoteMutation = useMutation({
    mutationFn: async (data: z.infer<typeof insertPriceQuoteSchema>) => {
      console.log('[Create Price Quote] Submitting data:', data);
      // Sending price quote data to server with validated date format
      const response = await apiRequest('POST', '/api/price-quotes', data);
      console.log('[Create Price Quote] Response:', response);
      return response;
    },
    onSuccess: (data) => {
      console.log('[Create Price Quote] Success:', data);
      queryClient.invalidateQueries({ queryKey: ['/api/price-quotes'] });
      toast({
        title: t('priceQuotes:created'),
        description: t('priceQuotes:createdSuccess'),
      });
      setIsCreateDialogOpen(false);
    },
    onError: (error: Error) => {
      console.error('[Create Price Quote] Error:', error);
      toast({
        title: t('common:error'),
        description: `${t('priceQuotes:createFailed')}: ${error.message}`,
        variant: 'destructive',
      });
    }
  });
  
  // Update price quote mutation
  const updatePriceQuoteMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<z.infer<typeof insertPriceQuoteSchema>> }) => {
      return apiRequest('PATCH', `/api/price-quotes/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/price-quotes'] });
      toast({
        title: t('priceQuotes:updated'),
        description: t('priceQuotes:updatedSuccess'),
      });
      setIsEditDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: t('common:error'),
        description: `${t('priceQuotes:updateFailed')}: ${error.message}`,
        variant: 'destructive',
      });
    }
  });

  // Event handlers
  const handleDeleteClick = (priceQuote: PriceQuote) => {
    setSelectedPriceQuote(priceQuote);
    setDeleteDialogOpen(true);
  };

  const handlePreviewClick = (priceQuote: PriceQuote) => {
    setSelectedPriceQuote(priceQuote);
    setIsPreviewDialogOpen(true);
  };

  const handleEmailClick = (priceQuote: PriceQuote) => {
    setSelectedPriceQuote(priceQuote);
    
    // Pre-fill email from client
    if (clients && priceQuote) {
      const client = clients.find(c => c.id === priceQuote.clientId);
      if (client && client.email) {
        setEmailAddress(client.email);
      } else {
        setEmailAddress('');
      }
    }
    
    setIsEmailDialogOpen(true);
  };

  // Fetch price quote items
  const fetchPriceQuoteItems = async (priceQuoteId: number) => {
    try {
      const response = await fetch(`/api/price-quotes/${priceQuoteId}/items`);
      if (!response.ok) {
        throw new Error('Failed to fetch price quote items');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching price quote items:', error);
      toast({
        title: t('common:error'),
        description: t('priceQuotes:errorFetchingItems'),
        variant: 'destructive',
      });
      return [];
    }
  };

  const handleEditClick = async (priceQuote: PriceQuote) => {
    console.log('[Price Quotes] Editing price quote:', priceQuote);
    const priceQuoteWithItems: PriceQuoteWithItems = { ...priceQuote };
    
    // Ensure clientId is set (handle both camelCase and snake_case)
    const clientId = priceQuote.clientId || (priceQuote as any).client_id;
    console.log('[Price Quotes] Client ID:', clientId);
    setSelectedClientId(clientId);
    priceQuoteWithItems.clientId = clientId;
    
    setSelectedPriceQuote(priceQuoteWithItems);
    
    // Load the price quote items before opening the edit dialog
    try {
      const items = await fetchPriceQuoteItems(priceQuote.id);
      console.log('[Price Quotes] Loaded items:', items);
      // Add items to the price quote object
      priceQuoteWithItems.items = items.length > 0 ? items : [{ name: "", quantity: 1, unitPrice: 0 }];
      setSelectedPriceQuote(priceQuoteWithItems);
      setIsEditDialogOpen(true);
    } catch (error) {
      console.error('Error preparing price quote for editing:', error);
      // Still open dialog even if items fail to load
      setIsEditDialogOpen(true);
    }
  };

  const handleSendEmail = () => {
    if (!selectedPriceQuote || !emailAddress) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailAddress)) {
      toast({
        title: t('priceQuotes:invalidEmail'),
        description: t('priceQuotes:invalidEmailDescription'),
        variant: 'destructive',
      });
      return;
    }
    
    updateStatusMutation.mutate({
      priceQuoteId: selectedPriceQuote.id,
      status: 'sent',
      email: emailAddress
    });
  };

  const handleMarkAsAccepted = (priceQuoteId: number) => {
    updateStatusMutation.mutate({
      priceQuoteId,
      status: 'accepted'
    });
  };

  const handleMarkAsRejected = (priceQuoteId: number) => {
    updateStatusMutation.mutate({
      priceQuoteId,
      status: 'rejected'
    });
  };

  const handleConvertToInvoice = (priceQuote: PriceQuote) => {
    convertToInvoiceMutation.mutate(priceQuote.id);
  };

  const handleDuplicatePriceQuote = (priceQuote: PriceQuote) => {
    duplicatePriceQuoteMutation.mutate(priceQuote.id);
  };

  const handlePrintToPdf = async () => {
    if (!selectedPriceQuote) return;
    
    setIsPrintingToPdf(true);
    try {
      const response = await fetch(`/api/price-quotes/${selectedPriceQuote.id}/pdf`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cenova-ponuka-${selectedPriceQuote.title || formatPriceQuoteNumber(selectedPriceQuote.id)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: t('common:error'),
        description: t('priceQuotes:pdfGenerationFailed'),
        variant: 'destructive',
      });
    } finally {
      setIsPrintingToPdf(false);
    }
  };

  return (
    <div className="page-container">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 section-spacing">
        <div className="min-w-0">
          <h1 className="page-title">{t('quotes:title')}</h1>
        </div>
        <Button 
          className="w-full sm:w-auto shrink-0"
          onClick={() => {
            console.log('Button clicked! Setting dialog to open');
            setIsCreateDialogOpen(true);
          }}
          type="button"
          style={{ position: 'relative', zIndex: 10 }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('priceQuotes:createNew')}
        </Button>
      </div>

      {/* Responzívne filtry - mobile stacked, desktop horizontal */}
      <div className="mb-6 bg-card p-3 sm:p-4 rounded-xl border shadow-sm backdrop-blur-sm min-w-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 items-end">
          {/* Search Filter */}
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="block text-sm font-medium mb-2 text-muted-foreground">{t('quotes:search')}</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('priceQuotes:searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 rounded-xl border-2 focus:border-primary transition-colors touch-manipulation"
              />
            </div>
          </div>

          {/* Sort By Filter */}
          <div>
            <label className="block text-sm font-medium mb-2 text-muted-foreground">{t('quotes:sortBy')}</label>
            <Select value={sortBy} onValueChange={(value: 'date' | 'client' | 'amount') => setSortBy(value)}>
              <SelectTrigger className="h-11 rounded-xl border-2 hover:border-primary/50 focus:border-primary transition-colors touch-manipulation">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">{t('quotes:date')}</SelectItem>
                <SelectItem value="client">{t('quotes:client')}</SelectItem>
                <SelectItem value="amount">{t('quotes:amount')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sort Order */}
          <div>
            <label className="block text-sm font-medium mb-2 text-muted-foreground">Smer</label>
            <Button
              variant="outline"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="w-full justify-start h-11 rounded-xl border-2 hover:border-primary/50 touch-manipulation"
            >
              <ArrowUpDown className="mr-2 h-4 w-4" />
              {sortOrder === 'asc' ? 'Vzostupne' : 'Zostupne'}
            </Button>
          </div>

          {/* Year Filter */}
          <div>
            <label className="block text-sm font-medium mb-2 text-muted-foreground">Rok</label>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="h-11 rounded-xl border-2 hover:border-primary/50 focus:border-primary transition-colors touch-manipulation">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všetky roky</SelectItem>
                {Array.isArray(availableYears) ? availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                )) : null}
              </SelectContent>
            </Select>
          </div>

          {/* Results count */}
          <div className="flex items-end">
            <div className="text-sm text-muted-foreground bg-muted px-3 py-2 rounded-lg whitespace-nowrap">
              {filteredAndSortedPriceQuotes.length} {t('common:of')} {priceQuotes?.length || 0} {t('priceQuotes:quotes')}
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <p>{t('common:loading')}...</p>
      ) : (
        <PriceQuoteTable
          priceQuotes={filteredAndSortedPriceQuotes}
          clients={clients || []}
          onPreview={handlePreviewClick}
          onDelete={handleDeleteClick}
          onEmail={handleEmailClick}
          onEdit={handleEditClick}
          onMarkAsAccepted={handleMarkAsAccepted}
          onMarkAsRejected={handleMarkAsRejected}
          onConvertToInvoice={handleConvertToInvoice}
          onDuplicate={handleDuplicatePriceQuote}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('priceQuotes:confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('priceQuotes:deleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => selectedPriceQuote && deleteMutation.mutate(selectedPriceQuote.id)}
              className="bg-red-500 hover:bg-red-600"
            >
              {t('common:delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Dialog */}
      <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Odoslať cenovú ponuku emailom</DialogTitle>
            <DialogDescription>
              Zadajte emailovú adresu na ktorú chcete odoslať cenovú ponuku. Ponuka bude odoslaná ako PDF príloha.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Client email suggestion */}
            {selectedPriceQuote && clients && (() => {
              const client = clients.find(c => c.id === selectedPriceQuote.clientId);
              if (client && client.email) {
                return (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800 font-medium mb-2">
                      Email klienta: {client.businessName || client.name}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEmailAddress(client.email!)}
                        className="text-xs"
                      >
                        Použiť: {client.email}
                      </Button>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            <div className="grid gap-2">
              <label htmlFor="email" className="text-sm font-medium">
                Emailová adresa príjemcu
              </label>
              <Input
                id="email"
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="email@klienta.sk"
                className="w-full"
              />
              <p className="text-xs text-gray-500">
                Ponuka bude odoslaná ako PDF príloha spolu s popisom služieb.
              </p>
            </div>

            {/* Preview info */}
            {selectedPriceQuote && (
              <div className="p-3 bg-muted border border-border rounded-md">
                <p className="text-sm font-medium text-foreground mb-1">
                  Odošle sa ponuka:
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedPriceQuote.title} - {formatPriceQuoteNumber(selectedPriceQuote.id)}
                </p>
                <p className="text-sm text-gray-600">
                  Suma: {formatCurrency(parseFloat(selectedPriceQuote.totalAmount || '0'))}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEmailDialogOpen(false)}>
              Zrušiť
            </Button>
            <Button 
              onClick={handleSendEmail}
              disabled={!emailAddress || !emailAddress.includes('@') || updateStatusMutation.isPending}
              className="bg-[#1e293b] text-white hover:bg-[#334155]"
            >
              {updateStatusMutation.isPending ? (
                <>
                  <Send className="mr-2 h-4 w-4 animate-pulse" />
                  Odosiela sa...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Odoslať email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price Quote Preview Dialog */}
      {selectedPriceQuote && (
        <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
          <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30 text-foreground print:p-0">
            <DialogHeader className="border-b pb-4 mb-4">
              <DialogTitle className="text-black">
                Náhľad cenovej ponuky
              </DialogTitle>
              <div className="flex space-x-2">
                <Button 
                  className="bg-[#1e293b] text-white hover:bg-[#334155] print:hidden"
                  size="sm" 
                  onClick={handlePrintToPdf}
                  disabled={isPrintingToPdf}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {isPrintingToPdf ? 'Generujem...' : 'Stiahnuť PDF'}
                </Button>
                <Button 
                  className="bg-[#1e293b] text-white hover:bg-[#334155] print:hidden"
                  size="sm"
                  onClick={() => {
                    const printWindow = window.open(
                      `/api/price-quotes/${selectedPriceQuote.id}/html?t=${Date.now()}`,
                      '_blank',
                      'width=900,height=1200,scrollbars=yes,resizable=yes'
                    );
                    if (printWindow) {
                      printWindow.onload = () => {
                        setTimeout(() => {
                          printWindow.print();
                        }, 500);
                      };
                    }
                  }}
                >
                  <Printer className="mr-2 h-4 w-4" />
                  Tlačiť
                </Button>
              </div>
            </DialogHeader>
            <iframe
              src={`/api/price-quotes/${selectedPriceQuote.id}/html?t=${Date.now()}`}
              className="w-full border-0"
              style={{ minHeight: '800px', height: 'calc(90vh - 200px)' }}
              title="Price Quote Preview"
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Create Price Quote Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} modal={false}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto bg-background/95 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
          <DialogHeader>
            <DialogTitle className="text-xl">{t('priceQuotes:createNew')}</DialogTitle>
            <DialogDescription>
              {t('priceQuotes:createDescription')}
            </DialogDescription>
          </DialogHeader>
          
          {!clients ? (
            <div className="py-6 text-center">
              <p className="text-muted-foreground mb-4">Načítavanie klientov...</p>
            </div>
          ) : !Array.isArray(clients) || clients.length === 0 ? (
            <div className="py-6 text-center">
              <p className="text-red-500 mb-4">{t('priceQuotes:noClientsWarning')}</p>
              <Button asChild variant="outline">
                <a href="/clients">{t('priceQuotes:createClientFirst')}</a>
              </Button>
            </div>
          ) : (
            <PriceQuoteForm
              onSubmit={(data) => {
                console.log('[Price Quotes Page] Form onSubmit called with:', data);
                const formattedData = {
                  ...data,
                  validUntil: data.validUntil || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                };
                console.log('[Price Quotes Page] Formatted data:', formattedData);
                createPriceQuoteMutation.mutate(formattedData);
              }}
              clients={clients}
              isSubmitting={createPriceQuoteMutation.isPending}
              onCancel={() => setIsCreateDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Price Quote Dialog */}
      {selectedPriceQuote && (
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('priceQuotes:edit')}</DialogTitle>
              <DialogDescription>
                {t('priceQuotes:editDescription')}
              </DialogDescription>
            </DialogHeader>
            
            {clients && (
              <PriceQuoteForm
                onSubmit={(data) => {
                  // Zabezpečíme, že validUntil nie je undefined, ak je potrebný
                  const formattedData = {
                    ...data,
                    validUntil: data.validUntil || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // ak nie je nastavený, použije sa +30 dní
                  };
                  updatePriceQuoteMutation.mutate({ id: selectedPriceQuote.id, data: formattedData });
                }}
                defaultValues={selectedPriceQuote}
                clients={clients}
                isSubmitting={updatePriceQuoteMutation.isPending}
                onCancel={() => setIsEditDialogOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
